import React from 'react';
import PropTypes from 'prop-types';
// import JSXStyle from 'styled-jsx/style';

import styles from './article.module.css';

import { ArticleContext } from './Context';

import { StyledImage } from './components/Image';
import { StyledAdvert } from './components/Advert';
import { StyledSpacer } from './components/Spacer';
import { StyledVideo } from './components/Video';
import { StyledDivider } from './components/Divider';
import { StyledTwitterTimeline } from './components/Twitter';
import { StyledText } from './components/Text';
import { Canvas } from './Canvas';
import { StyledInfogram } from './components/Infogram';
import { StyledColumns } from './components/Columns';
import { StyledGallery } from './components/Gallery';
import { StyledCustom } from './components/Custom';

import { Lightbox } from './lightbox/Lightbox';

// import { StyledMap } from './components/Map';

const breakpoints = {
	mobile: 768,
	tablet: 1024,
};

export class Article extends React.Component {
	ref = React.createRef();

	state = {
		width: null,
		height: null,
		device: 'mobile',
		StyleID: 0,
		isLightboxDisplayed: false,
	};

	componentDidMount() {
		const { id } = this.props;
		const element = this.ref.current;
		const width = element.clientWidth;
		const device =
			width < breakpoints.mobile + 1
				? 'mobile'
				: width < breakpoints.tablet
				? 'tablet'
				: 'desktop';
		const css = new CSS(
			id,
			this.props.styles,
			this.props.styles.get(this.props.style)
		);
		this.setState({
			width,
			height: element.clientHeight,
			device,
			css: css.build(),
		});

		// const observer = new MutationObserver(this.onResize);
		const observer = new ResizeObserver(this.onResize);
		observer.observe(element, {
			attributes: true,
			attributeOldValue: true,
			attributeFilter: ['style'],
		});
		element.addEventListener('resize', this.resizeListener);
	}

	componentDidUpdate(_, prevState) {
		const { onLightboxMode } = this.props;
		const { isLightboxDisplayed } = this.state;
		if (prevState.isLightboxDisplayed !== isLightboxDisplayed) {
			if (onLightboxMode) {
				onLightboxMode(isLightboxDisplayed);
			}
		}
	}

	onResize = (mutations) => {
		const el = mutations[0].target;
		const w = el.clientWidth;
		const h = el.clientHeight;

		const isChange = mutations
			.map((m) => `${m.oldValue}`)
			.some(
				(prev) =>
					prev.indexOf(`width: ${w}px`) === -1 ||
					prev.indexOf(`height: ${h}px`) === -1
			);

		if (!isChange) {
			return;
		}
		const event = new CustomEvent('resize', {
			detail: { width: w, height: h },
		});
		el.dispatchEvent(event);
	};

	resizeListener = (e) => {
		const { width, height } = e.detail;
		const device =
			width < breakpoints.mobile + 1
				? 'mobile'
				: width < breakpoints.tablet
				? 'tablet'
				: 'desktop';
		this.setState({
			width,
			height,
			device,
		});
	};

	byDevice = ({ devices }) => {
		const { device } = this.state;
		if (
			(devices.phone === 'off' && device === 'mobile') ||
			(devices.tablet === 'off' && device === 'tablet') ||
			(devices.desktop === 'off' && device === 'desktop')
		) {
			return false;
		}

		return true;
	};

	byComponent = (component, i) => {
		switch (component.component) {
			case 'headline':
			case 'title':
			case 'subtitle':
			case 'intro':
			case 'body':
			case 'crosshead':
			case 'byline':
			case 'blockquote':
			case 'text1':
			case 'text2':
			case 'text3':
			case 'text4':
			case 'text5':
			case 'text6':
			case 'text7':
			case 'text8':
			case 'text9':
			case 'text10':
			case 'footer':
				return <StyledText key={i} {...component} />;
			case 'image':
				return <StyledImage key={i} {...component} />;
			case 'advert':
				return <StyledAdvert key={i} {...component} />;
			case 'infogram':
				return <StyledInfogram key={i} {...component} />;
			case 'map':
				return null;
			// return <StyledMap key={i}  {...component}/>;
			case 'spacer':
				return <StyledSpacer key={i} {...component} />;
			case 'video':
				return <StyledVideo key={i} {...component} />;
			case 'twitter':
				return <StyledTwitterTimeline key={i} {...component} />;
			case 'divider':
				return <StyledDivider key={i} {...component} />;
			case 'columns':
				return <StyledColumns key={i} {...component} />;
			case 'gallery':
				if (component['control-speed']) {
					component.control_speed = component['control-speed'];
				}
				return <StyledGallery key={i} {...component} />;
			case 'custom':
				return <StyledCustom key={i} {...component} />;
			default:
				return null;
		}
	};

	componentWillUnmount() {
		const element = this.ref.current;
		element.removeEventListener('resize', this.resizeListener);
	}

	onLightboxClick = ({ id }) => {
		this.setState({ isLightboxDisplayed: true, lightboxId: id });
	};

	render() {
		let { id, components, lang, fontSize, style, index, isVoid } =
			this.props;

		const { width, css, isLightboxDisplayed, lightboxId } = this.state;
		let device =
			width < 769 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';

		const className = [styles['article'], `article-${id}`];
		className.push(device !== 'mobile' ? device : '');

		if (isVoid) {
			components = [];
		}

		return (
			<ArticleContext.Provider
				value={{
					device,
					width,
					styles: this.props.styles,
					style: this.props.styles.get(style),
					lang: lang ? lang : {},
					onLightboxClick: this.onLightboxClick,
				}}
			>
				<article
					id={`article-${id}`}
					ref={this.ref}
					data-index={index !== null ? index : null}
					style={{ fontSize: `${fontSize}%` }}
					className={className.join(' ')}
				>
					<Canvas>
						{components
							.filter(this.byDevice)
							.map(this.byComponent)
							.filter((i) => !!i)}
					</Canvas>
					{css ? <style jsx>{css}</style> : null}
					<Lightbox
						display={isLightboxDisplayed}
						selected={lightboxId}
						components={components}
						onClose={() => {
							this.setState({ isLightboxDisplayed: false });
						}}
					/>
				</article>
			</ArticleContext.Provider>
		);
	}
}

Article.propTypes = {
	id: PropTypes.string,
	style: PropTypes.string,
	index: PropTypes.number,
	name: PropTypes.object,
	lightbox: PropTypes.bool,
	styles: PropTypes.instanceOf(Map),
	components: PropTypes.array,
	lang: PropTypes.string,
	fontSize: PropTypes.number,
	isVoid: PropTypes.bool,
	onLightboxMode: PropTypes.any,
};

Article.defaultProps = {
	color: '#ffffff',
	backgroundColor: '#ffffff',
	paddingTop: 0,
	paddingBottom: 0,
	spacingUnit: 'px',
	fontSize: 62.5,
	horizontalPadding: 0,
	lang: 'en',
	styles: new Map(),
	components: [],
	isVoid: false,
};

class CSS {
	constructor(id, styles, style) {
		this.id = id;
		this.styles = styles;
		this.style = style;
	}

	build() {
		const devices = ['mobile', 'desktop', 'tablet'];
		if (this.id === '6690') {
			console.log(`Styles for article-${this.id}`);
			console.log(
				devices
					.map((device) => {
						let style = this.style;
						if (device === 'mobile') {
							return {
								device,
								style,
							};
						}

						style = this.styles.get(style[device]);
						return {
							device,
							style,
						};
					})
					.filter((i) => i.style)
					.map(this.mapDeviceStyle)
					.join('\n--------------------------------------------\n')
			);
		}
		const css = devices
			.map((device) => {
				let style = this.style;
				if (device === 'mobile') {
					return {
						device,
						style,
					};
				}

				style = this.styles.get(style[device]);
				return {
					device,
					style,
				};
			})
			.filter((i) => i.style)
			.map(this.mapDeviceStyle)
			.join('\n');
		return css;
	}

	mapDeviceStyle = ({ style, device }) => {
		const { paddingLeft, paddingRight } = this.getBleedPadding(style);
		const canvas = this.getCanvasCSS(
			style,
			paddingLeft,
			paddingRight,
			device
		);
		const bleed = this.getBleedCSS(paddingLeft, paddingRight, device);
		const caption = this.getCaptionCSS(style, device);
		const text = this.getTextCSS(style, device);
		const anchor = this.getAnchorCSS(style, device);
		const spacer = this.getSpacerCSS(style, device);

		const css = [canvas, bleed, caption, text, anchor, spacer]
			.filter((i) => i)
			.join('\n');

		return css;
	};

	getBleedPadding = (style) => {
		let { paddingLeft, paddingRight, spacingUnit } =
			style.properties['canvas'];

		if (!spacingUnit) {
			spacingUnit = 'px';
		}

		return {
			paddingLeft: paddingLeft ? `${paddingLeft}${spacingUnit}` : '0px',
			paddingRight: paddingRight
				? `${paddingRight}${spacingUnit}`
				: '0px',
		};
	};

	getCanvasCSS = (style, paddingLeft, paddingRight, device) => {
		const id = this.id;
		let {
			componentBottomMargin,
			bodyColor,
			color,
			paddingTop,
			paddingBottom,
			spacingUnit,
		} = style.properties['canvas'];

		const css = [];

		if (!spacingUnit) {
			spacingUnit = 'px';
		}

		if (paddingTop) {
			paddingTop = `${paddingTop}${spacingUnit}`;
		}
		if (paddingBottom) {
			paddingBottom = `${paddingBottom}${spacingUnit}`;
		}

		bodyColor = bodyColor
			? `background-color: ${bodyColor};`
			: 'background-color: #fff;';
		color = color ? `background-color: ${color};` : '';
		paddingTop = paddingTop ? `padding-top: ${paddingTop};` : '';
		paddingBottom = paddingBottom
			? `padding-bottom: ${paddingBottom};`
			: '';

		device = device !== 'mobile' ? `.${device}` : '';
		paddingLeft = `padding-left: ${paddingLeft};`;
		paddingRight = `padding-right: ${paddingRight};`;
		css.push(`.article-${id}${device}{
			${bodyColor}
		}`);
		css.push(`.article-${id}${device} > .canvas {
			display: block;
			height: 100%;
			${color}
			${paddingLeft}
			${paddingRight}
			${paddingTop}
			${paddingBottom}
		}`);
		if (componentBottomMargin) {
			css.push(`.article-${id}${device} > .canvas .media {
				margin-bottom: ${componentBottomMargin}px !important;
			}`);
		}

		return css.join('\n');
	};

	getBleedCSS = (paddingLeft, paddingRight, device) => {
		const id = this.id;
		device = device !== 'mobile' ? `.${device}` : '';
		const bleedLeft = paddingLeft ? `margin-left: -${paddingLeft};` : '';
		const bleedRight = paddingRight
			? `margin-right: -${paddingRight};`
			: '';
		return `.article-${id}${device} > .canvas .bleed {
			${bleedLeft}
			${bleedRight}
		}`;
	};

	getCaptionCSS = (style, device) => {
		const id = this.id;
		device = device !== 'mobile' ? `.${device}` : '';
		if (!style?.properties?.imagecaption) {
			return '';
		}
		let {
			color,
			fontFamily,
			fontSize,
			fontStyle,
			fontWeight,
			letterSpacing,
			lineHeight,
			marginBottom,
			textAlign,
			textDecoration,
			textTransform,
		} = style.properties['imagecaption'];

		color = color ? `color: ${color};` : '';
		fontFamily = fontFamily ? `font-family: ${fontFamily};` : '';
		fontSize = fontSize ? `font-size: ${fontSize}em;` : '';
		fontStyle = fontStyle ? `font-style: ${fontStyle};` : '';
		fontWeight = fontWeight ? `font-weight: ${fontWeight};` : '';
		letterSpacing = letterSpacing
			? `letter-spacing: ${parseFloat(letterSpacing)}em;`
			: '';
		lineHeight = lineHeight ? `line-height: ${lineHeight};` : '';
		marginBottom = marginBottom ? `margin-bottom: ${marginBottom};` : '';
		textAlign = textAlign ? `text-align: ${textAlign};` : '';
		textDecoration = textDecoration
			? `text-decoration: ${textDecoration};`
			: '';
		textTransform = textTransform
			? `text-transform: ${textTransform};`
			: '';

		return `.article-${id}${device} > .canvas > figure figcaption {
			${color}
			${fontFamily}
			${fontSize}
			${fontStyle}
			${fontWeight}
			${letterSpacing}
			${lineHeight}
			${marginBottom}
			${textAlign}
			${textDecoration}
			${textTransform}
		}`;
	};

	getCreditCSS = () => {};

	getTextCSS = (style, device) => {
		const id = this.id;
		device = device !== 'mobile' ? `.${device}` : '';
		const css = [''];
		const textComponents = [
			'headline',
			'title',
			'subtitle',
			'intro',
			'body',
			'crosshead',
			'byline',
			'blockquote',
			'footer',
			'text1',
			'text2',
			'text3',
			'text4',
			'text5',
			'text6',
			'text7',
			'text8',
			'text9',
			'text10',
		];
		// Mobile
		for (const component in style.properties) {
			// const properties = style.properties[property];
			// The component is not a text so we ignore it
			if (!textComponents.includes(component)) {
				continue;
			}

			const properties = style.properties[component];

			let {
				fontFamily,
				fontSize,
				color,
				fontWeight,
				fontStyle,
				textDecoration,
				lineHeight,
				textTransform,
				textAlign,
				letterSpacing,
				textDisplay,
				textIndent,
				contentAlign,
				width,
				// Dropcap
				dropcapFontSize,
				dropcapLineHeight,
				dropcapPaddingRight,
				dropcapColor,
				dropcapBackgroundColor,
				// Font Weight
				fontFamilyBold,
				fontFamilyItalic,
				fontFamilyBoldItalic,
				// Background color
				backgroundColor,
				backgroundColorEnabled,
				// Margin
				marginBottom,
				marginTop,
				// Padding
				paddingTop,
				paddingBottom,
				paddingLeft,
				paddingRight,
				// Border
				borderType,
				borderEdge,
				borderWidth,
				borderColor,
			} = properties;

			if (properties.css) {
				css.push(`.article-${id}${device}  > .canvas .${component} {
					${properties.css};
				}`);
			}

			textDisplay = textDisplay ? `display: ${textDisplay};` : '';

			if (backgroundColorEnabled === 'on' && backgroundColor) {
				css.push(`.article-${id}${device} > .canvas .${component} p{
					background: #${backgroundColor.replace('#', '')};
				}`);
			}

			css.push(`.article-${id}${device} > .canvas .${component} p {
				${device === 'mobile' ? 'margin: 0px;' : ''}
				${device === 'mobile' ? 'max-width: 100%;' : ''}
				${textDisplay}
			}
			.article-${id}${device} > .canvas .${component} > sub {
				font-size: 65%;
				line-height: 0;
				vertical-align: sub;
			}
			.article-${id}${device} > .canvas .${component} > sup{
				font-size: 65%;
				line-height: 0;
				vertical-align: super;
			}
			.article-${id}${device} > .canvas .${component} i {
				font-style: italic;
			}`);

			fontFamily = fontFamily ? `font-family: ${fontFamily};` : '';
			fontSize = fontSize ? `font-size: ${fontSize}em;` : '';
			color = color ? `color: ${color};` : '';
			fontWeight = fontWeight ? `font-weight: ${fontWeight};` : '';
			fontStyle = fontStyle ? `font-style: ${fontStyle};` : '';
			textDecoration = textDecoration
				? `text-decoration: ${textDecoration};`
				: '';
			lineHeight = lineHeight ? `line-height: ${lineHeight};` : '';
			textTransform = textTransform
				? `text-transform: ${textTransform};`
				: '';
			textAlign = textAlign ? `text-align: ${textAlign};` : '';
			letterSpacing = letterSpacing
				? `letter-spacing: ${parseFloat(letterSpacing)}em;`
				: '';
			marginBottom = marginBottom
				? `margin-bottom: ${marginBottom}px;`
				: '';
			marginTop = marginTop ? `margin-top: ${marginTop};` : '';

			dropcapLineHeight =
				dropcapLineHeight && dropcapFontSize
					? `line-height: ${
							parseFloat(dropcapFontSize + '') *
							parseFloat(dropcapLineHeight + '')
					  }em;`
					: '';
			dropcapFontSize = dropcapFontSize
				? `font-size: ${parseFloat(dropcapFontSize + '')}em;`
				: 'font-size: 4em;';
			dropcapPaddingRight = dropcapPaddingRight
				? `padding-right: ${dropcapPaddingRight}px;`
				: 'padding-right: 8px;';
			dropcapColor = dropcapColor ? `color: ${dropcapColor};` : '';
			dropcapBackgroundColor = dropcapBackgroundColor
				? `background: #${dropcapBackgroundColor.replace('#', '')};`
				: '';

			paddingTop = paddingTop ? `padding-top: ${paddingTop}%;` : '';
			paddingBottom = paddingBottom
				? `padding-bottom: ${paddingBottom}%;`
				: '';
			paddingLeft = paddingLeft ? `padding-left: ${paddingLeft}%;` : '';
			paddingRight = paddingRight
				? `padding-right: ${paddingRight}%;`
				: '';
			textIndent = textIndent ? `text-indent: ${textIndent}px;` : '';

			width = width ? `width: ${width}%;` : 'width: 100%';

			switch (contentAlign) {
				case 'left':
					contentAlign = `align-items: flex-start;`;
					break;
				default:
					contentAlign = 'align-items: center;';
			}

			let border = [];
			if (borderType !== 'none') {
				borderWidth = borderWidth ? `${borderWidth}px` : `1px`;
				borderColor = borderColor
					? `#${borderColor.replace('#', '')}`
					: `#000`;
				if (borderEdge) {
					switch (borderEdge) {
						case 'top':
							border.push(
								`border-top: ${borderWidth} ${borderType} ${borderColor};`
							);
							break;
						case 'left':
							border.push(
								`border-left: ${borderWidth} ${borderType} ${borderColor};`
							);
							break;
						case 'bottom':
							border.push(
								`border-bottom: ${borderWidth} ${borderType} ${borderColor};`
							);
							break;
						case 'right':
							border.push(
								`border-right: ${borderWidth} ${borderType} ${borderColor};`
							);
							break;
						case 'top-bottom':
							border.push(
								`border-top: ${borderWidth} ${borderType} ${borderColor};`
							);
							border.push(
								`border-bottom: ${borderWidth} ${borderType} ${borderColor};`
							);
							break;
						case 'left-bottom':
							border.push(
								`border-left: ${borderWidth} ${borderType} ${borderColor};`
							);
							border.push(
								`border-bottom: ${borderWidth} ${borderType} ${borderColor};`
							);
							break;
						default:
							border.push(
								`border: ${borderWidth} ${borderType} ${borderColor};`
							);
					}
				}
			}

			border = border.join('\n');

			css.push(`.article-${id}${device} > .canvas .${component} {
				display: flex;
				flex-direction: column;
				justify-content: center;
				${contentAlign};
				${border}
			}`);

			css.push(`.article-${id}${device} > .canvas .${component} p {
				overflow-wrap: break-word;
				${fontFamily}
				${fontSize}
				${color}
				${fontWeight}
				${fontStyle}
				${textDecoration}
				${lineHeight}
				${textTransform}
				${textAlign}
				${letterSpacing}
				${marginBottom}
				${marginTop}
				${paddingTop}
				${paddingBottom}
				${paddingLeft}
				${paddingRight}
				${textIndent}
				${width}
			}`);

			css.push(`.article-${id}${device}  > .canvas .${component}.dropcap > :first-child::first-letter {
				float: left;
				margin-left: -2px;
				${dropcapFontSize}
				${dropcapLineHeight}
				${dropcapPaddingRight}
				${dropcapColor}
				${dropcapBackgroundColor}
			}`);
			if (fontFamilyBold) {
				css.push(`.article-${id}${device}  > .canvas .${component} > p b {
					font-family: ${fontFamilyBold} !important;
					font-weight: normal;
				}`);
			}

			if (fontFamilyItalic) {
				css.push(`.article-${id}${device} > .canvas .${component} > p i {
					font-family: ${fontFamilyItalic};
					font-style: normal;
				}`);
			}

			if (fontFamilyBoldItalic) {
				css.push(`.article-${id}${device} > .canvas .${component} > p b i {
					font-family: ${fontFamilyBoldItalic};
					font-weight: normal;
					font-style: normal;
				}`);
			}
		}
		return css.join('\n');
	};

	getAnchorCSS = (style, device) => {
		const id = this.id;
		device = device !== 'mobile' ? `.${device}` : '';
		if (!style?.properties?.anchor) {
			return '';
		}
		let { fontStyle, color, fontWeight, textDecoration, textTransform } =
			style.properties['anchor'];

		color = color ? `color: ${color};` : '';
		fontStyle = fontStyle ? `font-style: ${fontStyle};` : '';
		fontWeight = fontWeight ? `font-weight: ${fontWeight};` : '';
		textDecoration = textDecoration
			? `text-decoration: ${textDecoration};`
			: '';
		textTransform = textTransform
			? `text-transform: ${textTransform};`
			: '';

		return `.article-${id}${device} > .canvas > a {
			${color}
			${fontStyle}
			${fontWeight}
			${textDecoration}
			${textTransform}
		}`;
	};

	getSpacerCSS = (style, device) => {
		const id = this.id;
		device = device !== 'mobile' ? ` .${device}` : '';
		if (!style?.properties?.spacer) {
			return '';
		}
		const css = [];
		let {
			vSmallHeight,
			smallHeight,
			mediumHeight,
			largeHeight,
			vLargeHeight,
		} = style.properties['spacer'];

		if (vSmallHeight) {
			css.push(`.article-${id}${device} > .canvas .spacer .v-small {
				height: ${vSmallHeight}px;
			}`);
		}
		if (smallHeight) {
			css.push(`.article-${id}${device} > .canvas .spacer.small {
				height: ${smallHeight}px;
			}`);
		}
		if (mediumHeight) {
			css.push(`.article-${id}${device} > .canvas .spacer.medium {
				height: ${mediumHeight}px;
			}`);
		}
		if (largeHeight) {
			css.push(`.article-${id}${device} > .canvas .spacer.large {
				height: ${largeHeight}px;
			}`);
		}
		if (vLargeHeight) {
			css.push(`.article-${id}${device} > .canvas .spacer.v-large {
				height: ${vLargeHeight}px;
			}`);
		}

		return css.join('\n');
	};
}
